import { FiBell, FiCheck } from "react-icons/fi";
import { formatCurrency } from "@/utils/formatters";

export interface SplitSummaryLabels {
  overline: string;
  title: string;
  description: string;
  committed: string;
  discretionary: string;
  committedNote: string;
  discretionaryNote: string;
  /** Texto bajo el mosaico comprometido con el % del gasto ya formateado. */
  percentOfSpend: (percent: string) => string;
}

export interface SplitSummaryProps {
  /** Gasto comprometido del ciclo (cuotas ya contratadas). */
  committed: number;
  /** Gasto discrecional del ciclo (lo que todavía puedes decidir). */
  discretionary: number;
  /** Mensaje de estado en texto; el tono `warning` marca el borde, no el color solo. */
  status?: { tone: "ok" | "warning"; title: string; detail?: string };
  labels: SplitSummaryLabels;
}

/** Comprometido vs. discrecional: barra de dos segmentos, dos mosaicos con marcador de forma y estado. El comprometido no va en rojo. */
export function SplitSummary({ committed, discretionary, status, labels }: SplitSummaryProps) {
  const total = committed + discretionary;
  const pct = (value: number) => (total > 0 ? ((value / total) * 100).toFixed(1) : "0.0");
  const committedShare = total > 0 ? (committed / total) * 100 : 0;
  const discretionaryShare = total > 0 ? (discretionary / total) * 100 : 0;

  return (
    <section aria-label={labels.title} className="rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] p-5 text-[var(--ds-ink)] md:p-6">
      <p className="m-0 text-[11px] font-medium uppercase leading-[14px] tracking-[0.14em] text-[var(--ds-ink-muted)]">{labels.overline}</p>
      <h3 className="m-0 mt-1 text-[22px] font-medium leading-7">{labels.title}</h3>
      <p className="m-0 mb-4 mt-1 text-sm leading-5 text-[var(--ds-ink-muted)]">{labels.description}</p>

      <div
        role="img"
        aria-label={`${labels.committed} ${formatCurrency(committed)}, ${labels.discretionary} ${formatCurrency(discretionary)}`}
        className="flex h-3 overflow-hidden rounded-[var(--ds-radius-pill)] bg-[var(--ds-line)]"
      >
        <div className="h-full bg-[var(--ds-card-mist)]" style={{ width: `${committedShare}%` }} />
        <div className="h-full bg-[var(--ds-card-rose)]" style={{ width: `${discretionaryShare}%` }} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-[var(--ds-radius-md)] border-[1.5px] border-solid border-[var(--ds-line)] p-4">
          <p className="m-0 flex items-center gap-2 text-[15px] font-medium leading-5">
            <span aria-hidden="true" className="size-2.5 flex-none bg-[var(--ds-card-mist)]" />
            {labels.committed}
          </p>
          <p className="m-0 mt-1 font-medium text-[26px] leading-8">{formatCurrency(committed)}</p>
          <p className="m-0 mt-0.5 text-[13px] leading-[18px] text-[var(--ds-ink-muted)]">
            {labels.committedNote} · {labels.percentOfSpend(pct(committed))}
          </p>
        </div>
        <div className="rounded-[var(--ds-radius-md)] border-[1.5px] border-solid border-[var(--ds-line)] p-4">
          <p className="m-0 flex items-center gap-2 text-[15px] font-medium leading-5">
            <span aria-hidden="true" className="size-2.5 flex-none rounded-full bg-[var(--ds-card-rose)]" />
            {labels.discretionary}
          </p>
          <p className="m-0 mt-1 font-medium text-[26px] leading-8">{formatCurrency(discretionary)}</p>
          <p className="m-0 mt-0.5 text-[13px] leading-[18px] text-[var(--ds-ink-muted)]">{labels.discretionaryNote}</p>
        </div>
      </div>

      {status && (
        <div
          role="status"
          className={[
            "mt-4 flex gap-3 rounded-[var(--ds-radius-md)] border-[1.5px] border-solid p-4 text-[15px] leading-[22px]",
            status.tone === "warning" ? "border-[var(--ds-warning)]" : "border-[var(--ds-line)]",
          ].join(" ")}
        >
          {status.tone === "ok" ? <FiCheck aria-hidden="true" className="mt-px size-5 flex-none" /> : <FiBell aria-hidden="true" className="mt-px size-5 flex-none" />}
          <div>
            <p className="m-0">{status.title}</p>
            {status.detail && <p className="m-0 mt-2 text-[var(--ds-ink-muted)]">{status.detail}</p>}
          </div>
        </div>
      )}
    </section>
  );
}
